/**
 * Dashboard Page (for Principal)
 * 
 * Comprehensive analytics dashboard with:
 * - Participation rate tracking
 * - Visual analytics and trend graphs
 * - Response aggregation by question
 * - Downloadable reports (CSV)
 * - Action planning tools
 * - Filter by date range and respondent type
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardService, subscribeToNewSubmissions } from '../services/supabase';

const Dashboard = () => {
  const navigate = useNavigate();
  const { profile, logout } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState([]);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [questionStats, setQuestionStats] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const subscriptionRef = useRef(null);
  const autoRefreshIntervalRef = useRef(null);
  
  // Filters
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });

  useEffect(() => {
    loadDashboardData();
    
    // Set up real-time subscription for assessment updates
    subscriptionRef.current = subscribeToNewSubmissions((newSession) => {
      console.log('Real-time update received:', newSession);
      // Refresh dashboard data when sessions are updated or completed
      if (newSession.status === 'completed' || newSession.status === 'in_progress') {
        loadDashboardData();
      }
    });
    
    // Set up auto-refresh polling every 30 seconds as backup
    autoRefreshIntervalRef.current = setInterval(() => {
      loadDashboardData();
    }, 30000);
    
    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const { sessions: sessionData, error } = await dashboardService.getSessions(filters);
      if (!error) {
        setSessions(sessionData || []);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  }, [filters]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    } else if (activeTab === 'sessions') {
      loadSessions();
    }
  }, [activeTab, loadSessions]);

  const loadDashboardData = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      }
      
      const [ratesResult, submissionsResult] = await Promise.all([
        dashboardService.getCompletionRates(),
        dashboardService.getRecentSubmissions(10),
      ]);

      if (!ratesResult.error) {
        setStats(ratesResult.rates || []);
      }

      if (!submissionsResult.error) {
        setRecentSubmissions(submissionsResult.submissions || []);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
      if (showRefreshIndicator) {
        setRefreshing(false);
      }
    }
  };

  const loadAnalytics = async () => {
    try {
      const { stats: questionData, error } = await dashboardService.getQuestionStats();
      if (!error) {
        setQuestionStats(questionData || []);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      status: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle commas and quotes in values
          return typeof value === 'string' && (value.includes(',') || value.includes('"'))
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExportSessions = () => {
    const exportData = sessions.map(s => ({
      Name: s.users?.full_name || 'Anonymous',
      Role: s.users?.role || 'N/A',
      Status: s.status,
      Progress: `${s.responses_count || 0}/${s.total_questions || 0}`,
      StartTime: s.start_time,
      EndTime: s.end_time || 'In Progress',
    }));
    exportToCSV(exportData, 'assessment_sessions');
  };

  const handleExportQuestionStats = () => {
    exportToCSV(questionStats, 'question_analytics');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleRefresh = () => {
    loadDashboardData(true);
    if (activeTab === 'analytics') {
      loadAnalytics();
    } else if (activeTab === 'sessions') {
      loadSessions();
    }
  };

  const getTotalParticipation = () => {
    if (!stats || stats.length === 0) return { total: 0, completed: 0, rate: 0 };
    const total = stats.reduce((sum, s) => sum + (s.total_sessions || 0), 0);
    const completed = stats.reduce((sum, s) => sum + (s.completed_sessions || 0), 0);
    return { total, completed, rate: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const participation = getTotalParticipation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">PKKS Analytics Dashboard</h1>
              <p className="text-blue-100 mt-1">Principal Performance Assessment System</p>
              <p className="text-sm text-blue-200 mt-1">Welcome, {profile?.full_name}</p>
            </div>
            <div className="flex gap-3 items-center">
              <Link
                to="/principal-summary"
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all font-medium border border-white/30 flex items-center gap-2"
              >
                <span>📊</span>
                Skor Kepala Sekolah
              </Link>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all font-medium border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                title="Refresh dashboard data"
              >
                <span className={refreshing ? 'animate-spin' : ''}>{refreshing ? '⟳' : '↻'}</span>
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                onClick={handleLogout}
                className="px-6 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex space-x-6">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'analytics', label: 'Question Analytics', icon: '📈' },
              { id: 'sessions', label: 'All Sessions', icon: '📋' },
              { id: 'reports', label: 'Reports', icon: '📄' },
              { id: 'actions', label: 'Action Planning', icon: '🎯' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-4 px-2 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                  }
                `}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Key Metrics */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium opacity-90">Total Participation</h3>
                  <span className="text-2xl">👥</span>
                </div>
                <p className="text-4xl font-bold mb-1">{participation.total}</p>
                <p className="text-sm opacity-75">Total assessments</p>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium opacity-90">Completed</h3>
                  <span className="text-2xl">✅</span>
                </div>
                <p className="text-4xl font-bold mb-1">{participation.completed}</p>
                <p className="text-sm opacity-75">{participation.rate}% completion rate</p>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium opacity-90">In Progress</h3>
                  <span className="text-2xl">⏳</span>
                </div>
                <p className="text-4xl font-bold mb-1">{participation.total - participation.completed}</p>
                <p className="text-sm opacity-75">Active assessments</p>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium opacity-90">Response Rate</h3>
                  <span className="text-2xl">📊</span>
                </div>
                <p className="text-4xl font-bold mb-1">{participation.rate}%</p>
                <p className="text-sm opacity-75">Overall engagement</p>
              </div>
            </div>

            {/* Completion by Role */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Participation by Role</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className="border-l-4 border-blue-500 bg-gray-50 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {stat.role?.replace(/_/g, ' ').toUpperCase()}
                      </h3>
                      <span className="text-3xl">
                        {stat.role === 'teacher' ? '👨‍🏫' : stat.role === 'student' ? '👨‍🎓' : '🏢'}
                      </span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span className="font-semibold">{stat.completion_rate || 0}%</span>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all"
                          style={{ width: `${stat.completion_rate || 0}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white p-3 rounded">
                        <p className="text-gray-600 text-xs">Completed</p>
                        <p className="text-lg font-bold text-green-600">{stat.completed_sessions || 0}</p>
                      </div>
                      <div className="bg-white p-3 rounded">
                        <p className="text-gray-600 text-xs">Total</p>
                        <p className="text-lg font-bold text-gray-800">{stat.total_sessions || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Submissions */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Recent Activity</h2>
                <button
                  onClick={() => setActiveTab('sessions')}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  View All →
                </button>
              </div>
              
              {recentSubmissions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg mb-2">No submissions yet</p>
                  <p className="text-gray-400 text-sm">Assessment responses will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-4 px-4 font-semibold text-gray-700">Participant</th>
                        <th className="text-left py-4 px-4 font-semibold text-gray-700">Role</th>
                        <th className="text-left py-4 px-4 font-semibold text-gray-700">Status</th>
                        <th className="text-left py-4 px-4 font-semibold text-gray-700">Progress</th>
                        <th className="text-left py-4 px-4 font-semibold text-gray-700">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSubmissions.map((submission) => (
                        <tr key={submission.session_id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                          <td className="py-4 px-4 font-medium">{submission.full_name || 'Anonymous'}</td>
                          <td className="py-4 px-4">
                            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {submission.role}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              submission.status === 'completed' 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {submission.status}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full max-w-[100px]">
                                <div
                                  className="h-full bg-blue-600 rounded-full"
                                  style={{
                                    width: `${(submission.responses_count / submission.total_questions) * 100}%`
                                  }}
                                />
                              </div>
                              <span className="text-sm text-gray-600 whitespace-nowrap">
                                {submission.responses_count} / {submission.total_questions}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-600">
                            {new Date(submission.start_time).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Question Analytics</h2>
                <p className="text-gray-600 mt-1">Response aggregation and statistics by question</p>
              </div>
              <button
                onClick={handleExportQuestionStats}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                disabled={questionStats.length === 0}
              >
                <span>📥</span>
                <span>Export CSV</span>
              </button>
            </div>

            {questionStats.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No question data available yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {questionStats.map((q, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-gray-800 flex-1 pr-4">{q.question_text}</h3>
                      <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full whitespace-nowrap">
                        {q.response_count} responses
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4 mt-4">
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="text-xs text-gray-600 mb-1">Average Rating</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {q.avg_rating ? Number(q.avg_rating).toFixed(2) : 'N/A'}
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded">
                        <p className="text-xs text-gray-600 mb-1">Highest Rating</p>
                        <p className="text-2xl font-bold text-green-600">{q.max_rating || 'N/A'}</p>
                      </div>
                      <div className="bg-orange-50 p-3 rounded">
                        <p className="text-xs text-gray-600 mb-1">Lowest Rating</p>
                        <p className="text-2xl font-bold text-orange-600">{q.min_rating || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">All Assessment Sessions</h2>
              <button
                onClick={handleExportSessions}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                disabled={sessions.length === 0}
              >
                <span>📥</span>
                <span>Export CSV</span>
              </button>
            </div>

            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Roles</option>
                    <option value="teacher">Teacher</option>
                    <option value="student">Student</option>
                    <option value="industry_partner">Industry Partner</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="not_started">Not Started</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Sessions Table */}
            {sessions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No sessions found matching filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Participant</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Progress</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Started</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => (
                      <tr key={session.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">{session.users?.full_name || 'Anonymous'}</td>
                        <td className="py-3 px-4">
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {session.users?.role || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-2 py-1 rounded text-xs ${
                            session.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : session.status === 'in_progress'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {session.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {session.current_question_index + 1} / {session.total_questions}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {session.start_time ? new Date(session.start_time).toLocaleString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {session.end_time ? new Date(session.end_time).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Reports & Downloads</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
                <div className="text-center">
                  <span className="text-5xl mb-4 block">📊</span>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Participation Report</h3>
                  <p className="text-gray-600 text-sm mb-4">Download comprehensive participation statistics</p>
                  <button
                    onClick={() => {
                      const exportData = stats.map(s => ({
                        Role: s.role,
                        Total: s.total_sessions,
                        Completed: s.completed_sessions,
                        CompletionRate: `${s.completion_rate}%`
                      }));
                      exportToCSV(exportData, 'participation_report');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Generate Report
                  </button>
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
                <div className="text-center">
                  <span className="text-5xl mb-4 block">📈</span>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Analytics Report</h3>
                  <p className="text-gray-600 text-sm mb-4">Download detailed question analytics</p>
                  <button
                    onClick={handleExportQuestionStats}
                    disabled={questionStats.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Generate Report
                  </button>
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
                <div className="text-center">
                  <span className="text-5xl mb-4 block">📋</span>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Sessions Export</h3>
                  <p className="text-gray-600 text-sm mb-4">Export all session data with filters</p>
                  <button
                    onClick={() => setActiveTab('sessions')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Go to Sessions
                  </button>
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
                <div className="text-center">
                  <span className="text-5xl mb-4 block">📄</span>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Custom Report</h3>
                  <p className="text-gray-600 text-sm mb-4">Create a custom report with specific criteria</p>
                  <button
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg cursor-not-allowed"
                    disabled
                  >
                    Coming Soon
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Planning Tab */}
        {activeTab === 'actions' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Action Planning Tools</h2>
            
            <div className="space-y-6">
              {/* Key Insights */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="mr-2">💡</span>
                  Key Insights
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Overall participation rate: <strong>{participation.rate}%</strong></li>
                  <li>• Total completed assessments: <strong>{participation.completed}</strong></li>
                  <li>• Active assessments: <strong>{participation.total - participation.completed}</strong></li>
                  {stats.map(s => (
                    <li key={s.role}>
                      • {s.role?.replace(/_/g, ' ')} completion rate: <strong>{s.completion_rate}%</strong>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Recommendations */}
              <div className="bg-green-50 border-l-4 border-green-500 p-5 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="mr-2">✅</span>
                  Recommended Actions
                </h3>
                <div className="space-y-3">
                  {participation.rate < 50 && (
                    <div className="bg-white p-3 rounded">
                      <p className="font-medium text-gray-800">Boost Participation</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Current participation is below 50%. Consider sending reminder notifications or extending the assessment period.
                      </p>
                    </div>
                  )}
                  {stats.some(s => (s.completion_rate || 0) < 30) && (
                    <div className="bg-white p-3 rounded">
                      <p className="font-medium text-gray-800">Follow up with Low-Response Groups</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Some respondent groups have low completion rates. Consider targeted outreach or addressing potential barriers.
                      </p>
                    </div>
                  )}
                  {participation.rate >= 70 && (
                    <div className="bg-white p-3 rounded">
                      <p className="font-medium text-gray-800">Analyze Results</p>
                      <p className="text-sm text-gray-600 mt-1">
                        With {participation.rate}% participation, you have sufficient data to analyze trends and identify areas for improvement.
                      </p>
                    </div>
                  )}
                  <div className="bg-white p-3 rounded">
                    <p className="font-medium text-gray-800">Review Question Analytics</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Check the Analytics tab to identify questions with high or low ratings and plan targeted interventions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Items Template */}
              <div className="bg-purple-50 border-l-4 border-purple-500 p-5 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="mr-2">🎯</span>
                  Action Items Template
                </h3>
                <div className="bg-white p-4 rounded space-y-3 text-sm">
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" className="mt-1" />
                    <span className="text-gray-700">Review question analytics and identify top 3 areas needing attention</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" className="mt-1" />
                    <span className="text-gray-700">Schedule meetings with stakeholders to discuss findings</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" className="mt-1" />
                    <span className="text-gray-700">Create improvement action plan based on feedback</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" className="mt-1" />
                    <span className="text-gray-700">Send follow-up to incomplete assessments</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" className="mt-1" />
                    <span className="text-gray-700">Share summary report with leadership team</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
